<script setup lang="ts">
import { nextTick, onMounted, useTemplateRef } from "vue";

import { useModalStore } from "@/store/modal";
import { useSightlineStore } from "@/store/sightline";

import SightlineChatInput from "./SightlineChatInput.vue";
import SightlineSuggestions from "./SightlineSuggestions.vue";
import SightlineChartBlock from "./blocks/SightlineChartBlock.vue";
import SightlineTableBlock from "./blocks/SightlineTableBlock/SightlineTableBlock.vue";
import SightlineTextBlock from "./blocks/SightlineTextBlock.vue";

const sightlineStore = useSightlineStore();
const messagesRef = useTemplateRef("messagesEl");

onMounted(scrollToLatest);

async function submit(message: string) {
  scrollToLatest();
  await sightlineStore.ask(message);
  scrollToQuestion();
}

function toggleResult(messageIndex: number, blockIndex: number) {
  const modalStore = useModalStore();
  const attrs = modalStore.modal?.attrs;
  if (
    modalStore.isModalOpen("SightlineResultDialog") &&
    attrs?.transcriptId === sightlineStore.transcriptId &&
    Number(attrs?.messageIndex) === messageIndex &&
    Number(attrs?.blockIndex) === blockIndex
  ) {
    modalStore.close();
    return;
  }
  modalStore.open("SightlineResultDialog", {
    attrs: {
      transcriptId: sightlineStore.transcriptId,
      messageIndex,
      blockIndex,
      setReturnFocus: `#${triggerId(messageIndex, blockIndex)}`,
    },
  });
}

function triggerId(messageIndex: number, blockIndex: number) {
  return `sightline-result-trigger-${messageIndex}-${blockIndex}`;
}

function scrollToLatest() {
  void nextTick(() => {
    const messagesEl = messagesRef.value;
    if (!messagesEl) return;
    messagesEl.scrollTo({ top: messagesEl.scrollHeight });
  });
}

// A long answer must be read from its start: pin the question to the top of
// the viewport so the answer flows below it.
function scrollToQuestion() {
  void nextTick(() => {
    const messagesEl = messagesRef.value;
    if (!messagesEl) return;
    const bubbles = messagesEl.querySelectorAll<HTMLElement>(".user-bubble");
    const question = bubbles[bubbles.length - 1];
    if (!question) return;
    messagesEl.scrollTo({ top: question.offsetTop - 8 });
  });
}
</script>

<template>
  <div class="sightline-chat">
    <div ref="messagesEl" class="messages" aria-live="polite">
      <SightlineSuggestions v-if="sightlineStore.messages.length === 0" @submit="submit" />
      <template v-for="(message, index) in sightlineStore.messages" :key="index">
        <div v-if="message.role === 'user'" class="user-bubble">{{ message.text }}</div>
        <template v-else>
          <template v-for="(block, blockIndex) in message.blocks" :key="`${index}-${blockIndex}`">
            <SightlineTextBlock
              v-if="block.type === 'text'"
              :block
              :error="message.isError"
              :class="{
                'table-section-start':
                  blockIndex > 0 && message.blocks[blockIndex + 1]?.type === 'table',
              }"
            />
            <SightlineTableBlock
              v-else-if="block.type === 'table'"
              :block
              :trigger-id="triggerId(index, blockIndex)"
              :class="{
                'table-section-start':
                  blockIndex > 0 && message.blocks[blockIndex - 1]?.type !== 'text',
              }"
              @open="toggleResult(index, blockIndex)"
            />
            <SightlineChartBlock
              v-else-if="block.type === 'chart'"
              :block
              :trigger-id="triggerId(index, blockIndex)"
              @open="toggleResult(index, blockIndex)"
            />
          </template>
        </template>
      </template>
      <div
        v-if="sightlineStore.pending"
        class="thinking"
        role="status"
        :aria-label="$t('sightline.thinking')"
      >
        <span v-for="n in 3" :key="n" class="dot" />
      </div>
    </div>
    <SightlineChatInput
      class="input"
      :pending="sightlineStore.pending"
      @submit="submit"
      @stop="sightlineStore.stop"
    />
    <p class="disclaimer">{{ $t("sightline.disclaimer") }}</p>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.sightline-chat {
  display: flex;
  flex-direction: column;
  position: absolute;
  inset: 0;
  user-select: text;
  container-type: inline-size;

  .messages {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    padding: 8px 16px;

    @container (min-width: 640px) {
      padding-inline: 24px;
    }
  }

  .table-section-start {
    margin-top: 12px;
  }

  .input {
    margin: 0 16px;

    @container (min-width: 640px) {
      margin-inline: 24px;
    }
  }

  .disclaimer {
    @include typography.extra-small("regular");

    margin: 6px 16px 8px;
    text-align: center;
    color: colors.$text-subtle;

    @container (min-width: 640px) {
      margin-inline: 24px;
    }
  }

  .user-bubble {
    @include typography.medium;

    align-self: flex-end;
    max-width: 85%;
    margin: 8px 0;
    padding: 8px 12px;
    border-radius: 10px 10px 2px;
    background: colors.$background-grey-light;
    color: colors.$text;
    white-space: pre-wrap;
    overflow-wrap: break-word;
  }

  .thinking {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 10px 0;

    .dot {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: colors.$text-placeholder;
      animation: thinking-bounce 1.2s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.15s;
      }

      &:nth-child(3) {
        animation-delay: 0.3s;
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }
}

@keyframes thinking-bounce {
  0%,
  60%,
  100% {
    transform: none;
  }

  30% {
    transform: translateY(-4px) scale(1.25);
  }
}
</style>
